import { privateApiClient } from '../services/apiClient';
import { normalizePosMethod, methodPhone, resolveOperator } from './operator';

const TARGET_LABELS = { payment_request: 'Paiement au terminal', payment_link: 'Lien de paiement', invoice: 'Facture' };
const SUCCESS = ['paid', 'cashed'];

export const PERIODS = [
    { id: 'today', label: "Aujourd'hui" },
    { id: 'yesterday', label: 'Hier' },
    { id: 'week', label: '7 derniers jours' },
    { id: 'month', label: 'Ce mois' },
    { id: 'custom', label: 'Personnalisée' },
];

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

/** Bornes [from, to] d'une période ; `custom` utilise les dates saisies (AAAA-MM-JJ). */
export const periodRange = (period, customFrom, customTo) => {
    const now = new Date();
    switch (period) {
        case 'yesterday': {
            const y = new Date(now); y.setDate(y.getDate() - 1);
            return { from: startOfDay(y), to: endOfDay(y) };
        }
        case 'week': {
            const f = new Date(now); f.setDate(f.getDate() - 6);
            return { from: startOfDay(f), to: endOfDay(now) };
        }
        case 'month':
            return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: endOfDay(now) };
        case 'custom':
            return {
                from: customFrom ? startOfDay(new Date(`${customFrom}T00:00:00`)) : startOfDay(now),
                to: customTo ? endOfDay(new Date(`${customTo}T00:00:00`)) : endOfDay(now),
            };
        default:
            return { from: startOfDay(now), to: endOfDay(now) };
    }
};

const normalizePayment = (tr) => {
    const method = normalizePosMethod(tr.method);
    const client = tr.client || {};
    const clientName = client.name && !/^anonyme$/i.test(client.name) ? client.name : null;
    const phone = methodPhone(method)
        || (client.phoneNumber ? `${client.zip ? `+${client.zip} ` : ''}${client.phoneNumber}` : null);
    const status = String(tr.status || 'pending').toLowerCase();
    return {
        id: tr.id,
        reference: tr.trans_code || tr.code || tr.id,
        receiptId: tr.trans_id || null,
        amount: Number(tr.amount) || 0,
        currency: String(tr.currency || 'CDF').toUpperCase(),
        status,
        success: SUCCESS.includes(status),
        createdAt: tr.create_at ? new Date(tr.create_at) : null,
        paidAt: tr.paid_at ? new Date(tr.paid_at) : null,
        target: tr.target,
        channel: TARGET_LABELS[tr.target] || 'Autre',
        operator: resolveOperator({ name: method.providerName, providerId: method.providerId, phone }),
        payerName: clientName || phone || 'Client',
        payerPhone: phone,
        cashierId: tr.accountable || null,
        terminalId: tr.target === 'payment_request' ? tr.service : null,
        label: tr.label || '',
    };
};

/**
 * Paiements de l'organisation créés dans [from, to] (pos-service, triés du plus récent au plus ancien).
 * L'API n'a pas de filtre de date : on pagine jusqu'à dépasser le début de la période.
 */
export const fetchPaymentsInRange = async (orgId, from, to) => {
    const payments = [];
    for (let page = 1; page <= 20; page += 1) {
        const { data } = await privateApiClient.get('/pos/transaction-requests', {
            params: { org: orgId, pge: page, lmt: 500, tgt: 'all', sts: 'all', slg: '', cmr: 'all', emt: 'all' },
        });
        const contents = (data?.contents || []).map(normalizePayment);
        contents.forEach((p) => {
            if (p.createdAt && p.createdAt >= from && p.createdAt <= to) payments.push(p);
        });
        const oldest = contents[contents.length - 1]?.createdAt;
        if (!contents.length || !data?.nextPage || (oldest && oldest < from)) break;
    }
    return payments;
};

const addTo = (map, key, label, p) => {
    const row = map.get(key) || { key, label, count: 0, totals: {} };
    row.count += 1;
    row.totals[p.currency] = (row.totals[p.currency] || 0) + p.amount;
    map.set(key, row);
};

/** Agrégats du rapport de caisse (les montants encaissés ne comptent que les paiements réussis). */
export const buildCashReport = (payments, { cashierNameById = {} } = {}) => {
    const success = payments.filter((p) => p.success);
    const byCurrency = {};
    success.forEach((p) => {
        const c = byCurrency[p.currency] || { collected: 0, count: 0 };
        c.collected += p.amount;
        c.count += 1;
        byCurrency[p.currency] = c;
    });
    Object.values(byCurrency).forEach((c) => { c.average = c.count ? c.collected / c.count : 0; });

    const statusCounts = { success: success.length, pending: 0, failed: 0, expired: 0 };
    payments.forEach((p) => {
        if (p.status === 'pending') statusCounts.pending += 1;
        else if (p.status === 'failed') statusCounts.failed += 1;
        else if (p.status === 'expired') statusCounts.expired += 1;
    });

    const channels = new Map();
    const operators = new Map();
    const cashiers = new Map();
    const hours = Array.from({ length: 24 }, (_, h) => ({ hour: h, count: 0, totals: {} }));
    success.forEach((p) => {
        addTo(channels, p.target || 'other', p.channel, p);
        addTo(operators, p.operator || 'unknown', p.operator || 'Non précisé', p);
        addTo(cashiers, p.cashierId || 'unknown', cashierNameById[p.cashierId] || (p.cashierId ? 'Caissier' : 'Non attribué'), p);
        const at = p.paidAt || p.createdAt;
        if (at) {
            const slot = hours[at.getHours()];
            slot.count += 1;
            slot.totals[p.currency] = (slot.totals[p.currency] || 0) + p.amount;
        }
    });
    const sortRows = (m) => Array.from(m.values()).sort((a, b) => b.count - a.count);

    return {
        total: payments.length,
        successRate: payments.length ? Math.round((success.length / payments.length) * 100) : 0,
        byCurrency,
        statusCounts,
        byChannel: sortRows(channels),
        byOperator: sortRows(operators),
        byCashier: sortRows(cashiers),
        byHour: hours,
    };
};

export const money = (amount, currency) =>
    `${Number(amount || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${currency}`;

export const STATUS_LABELS = { paid: 'Payé', cashed: 'Encaissé', pending: 'En attente', failed: 'Échoué', expired: 'Expiré' };

const fmtDate = (d) => (d ? d.toLocaleDateString('fr-FR') : '');
const fmtTime = (d) => (d ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '');

/** Export CSV (séparateur « ; » et BOM pour une ouverture directe dans Excel en français). */
export const downloadCsv = (payments, filename, cashierNameById = {}) => {
    const header = ['Date', 'Heure', 'Référence', 'Client', 'Téléphone', 'Canal', 'Opérateur', 'Montant', 'Devise', 'Statut', 'Caissier', 'Motif'];
    const esc = (v) => {
        const s = String(v ?? '');
        return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = payments.map((p) => {
        const at = p.paidAt || p.createdAt;
        return [fmtDate(at), fmtTime(at), p.reference, p.payerName, p.payerPhone || '', p.channel, p.operator || '',
            String(p.amount).replace('.', ','), p.currency, STATUS_LABELS[p.status] || p.status,
            cashierNameById[p.cashierId] || '', p.label].map(esc).join(';');
    });
    const blob = new Blob([`﻿${[header.join(';'), ...lines].join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/** Rapport imprimable / PDF, au design des reçus Flow. */
export const buildReportHtml = ({ report, payments, organization, periodLabel, scopeLabel, cashierNameById = {} }) => {
    const origin = window.location.origin;
    const orderCurrencies = (list) => [...list].sort((x, y) => (x === 'CDF' ? -1 : y === 'CDF' ? 1 : x.localeCompare(y)));
    const currencies = Object.keys(report.byCurrency).length ? orderCurrencies(Object.keys(report.byCurrency)) : ['CDF'];
    const totalsCells = (totals) => currencies.map((c) => `<td class="num">${escapeHtml(money(totals[c] || 0, c))}</td>`).join('');
    const table = (title, rows) => `
      <h3>${escapeHtml(title)}</h3>
      <table><thead><tr><th>Libellé</th><th class="num">Paiements</th>${currencies.map((c) => `<th class="num">Total ${c}</th>`).join('')}</tr></thead>
      <tbody>${rows.length ? rows.map((r) => `<tr><td>${escapeHtml(r.label)}</td><td class="num">${r.count}</td>${totalsCells(r.totals)}</tr>`).join('')
        : `<tr><td colspan="${2 + currencies.length}" class="empty">Aucun paiement réussi</td></tr>`}</tbody></table>`;

    return `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8" />
<title>Rapport de caisse - ${escapeHtml(organization?.name || '')} - ${escapeHtml(periodLabel)}</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  :root { color-scheme: light; }
  body { margin: 0; background: #fff; font-family: 'Segoe UI', Roboto, Arial, sans-serif; color: #0F172A; font-size: 12px; }
  @media screen { body { max-width: 900px; margin: 24px auto; padding: 0 16px; } }
  .band { padding: 20px 24px; color: #fff; border-radius: 14px; border-bottom: 4px solid #EAB308;
          background: radial-gradient(120% 140% at 100% 0%, rgba(234,179,8,.25) 0%, rgba(234,179,8,0) 55%), linear-gradient(135deg, #0B1220, #1E293B); }
  .band-top { display: flex; justify-content: space-between; align-items: center; }
  .band img { height: 32px; }
  .tag { font-size: 10px; font-weight: 700; letter-spacing: .22em; text-transform: uppercase; color: #FACC15; border: 1px solid rgba(250,204,21,.45); padding: 5px 10px; border-radius: 999px; }
  .band h1 { margin: 16px 0 2px; font-size: 20px; }
  .band p { margin: 0; color: #CBD5E1; }
  .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 16px 0; }
  .kpi { border: 1px solid #E2E8F0; border-radius: 12px; padding: 12px; }
  .kpi span { display: block; font-size: 10px; text-transform: uppercase; letter-spacing: .1em; color: #64748B; }
  .kpi b { display: block; margin-top: 4px; font-size: 17px; }
  h3 { margin: 18px 0 6px; font-size: 13px; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .06em; color: #64748B; border-bottom: 1px solid #E2E8F0; padding: 6px 4px; }
  td { padding: 6px 4px; border-bottom: 1px solid #F1F5F9; }
  .num { text-align: right; white-space: nowrap; }
  .empty { text-align: center; color: #94A3B8; padding: 12px; }
  .mono { font-family: Consolas, monospace; }
  .foot { margin-top: 18px; font-size: 10px; color: #94A3B8; text-align: center; }
</style></head><body>
  <div class="band">
    <div class="band-top"><img src="${origin}/images/Logo_Netikash%20Flow%20Color.png" alt="Flow" /><span class="tag">Rapport de caisse</span></div>
    <h1>${escapeHtml(organization?.name || '')}</h1>
    <p>${escapeHtml(periodLabel)} · ${escapeHtml(scopeLabel)}</p>
  </div>
  <div class="kpis">
    ${currencies.map((c) => `<div class="kpi"><span>Encaissé ${c}</span><b>${escapeHtml(money(report.byCurrency[c]?.collected || 0, c))}</b></div>`).join('')}
    <div class="kpi"><span>Paiements réussis</span><b>${report.statusCounts.success} / ${report.total}</b></div>
    <div class="kpi"><span>Taux de réussite</span><b>${report.successRate} %</b></div>
  </div>
  <p>En attente : ${report.statusCounts.pending} · Échoués : ${report.statusCounts.failed} · Expirés : ${report.statusCounts.expired}</p>
  ${table('Par canal', report.byChannel)}
  ${table('Par opérateur', report.byOperator)}
  ${table('Par caissier', report.byCashier)}
  <h3>Détail des paiements (${payments.length})</h3>
  <table><thead><tr><th>Date</th><th>Référence</th><th>Client</th><th>Canal</th><th>Opérateur</th><th>Statut</th><th class="num">Montant</th></tr></thead><tbody>
  ${payments.map((p) => {
        const at = p.paidAt || p.createdAt;
        return `<tr><td>${escapeHtml(`${fmtDate(at)} ${fmtTime(at)}`)}</td><td class="mono">${escapeHtml(p.reference)}</td><td>${escapeHtml(p.payerName)}</td>
        <td>${escapeHtml(p.channel)}</td><td>${escapeHtml(p.operator || '—')}</td><td>${escapeHtml(STATUS_LABELS[p.status] || p.status)}</td>
        <td class="num">${escapeHtml(money(p.amount, p.currency))}</td></tr>`;
    }).join('') || '<tr><td colspan="7" class="empty">Aucun paiement sur la période</td></tr>'}
  </tbody></table>
  <div class="foot">Rapport généré par Flow by Netikash le ${escapeHtml(new Date().toLocaleString('fr-FR'))}${Object.keys(cashierNameById).length ? '' : ''}</div>
</body></html>`;
};

/** Impression / PDF via un iframe caché (attend le logo). */
export const printHtml = (html) => {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const win = iframe.contentWindow;
    win.document.open();
    win.document.write(html);
    win.document.close();
    let done = false;
    const print = () => {
        if (done) return;
        done = true;
        win.focus();
        win.print();
        setTimeout(() => iframe.remove(), 1000);
    };
    const img = win.document.images[0];
    if (img && !img.complete) {
        img.addEventListener('load', print);
        img.addEventListener('error', print);
        setTimeout(print, 3000);
    } else {
        setTimeout(print, 50);
    }
};
